import React, { useMemo, useRef, useState } from "react";
import { Button } from "antd";
import { aiChat } from "../../api/aiApi";

type Role = "user" | "assistant";
type ChatMsg = { id: string; role: Role; content: string };

const AiBot: React.FC = () => {
  const [messages, setMessages] = useState<ChatMsg[]>([
    { id: crypto.randomUUID(), role: "assistant", content: "Hey! Ask me anything 🙂" },
  ]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);

  const bottomRef = useRef<HTMLDivElement | null>(null);

  const canSend = useMemo(
    () => input.trim().length > 0 && !isSending,
    [input, isSending]
  );

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  };

  async function sendMessage() {
    const text = input.trim();
    if (!text || isSending) return;

    setInput("");
    setIsSending(true);

    const userMsg: ChatMsg = { id: crypto.randomUUID(), role: "user", content: text };

    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    scrollToBottom();

    try {
      const apiMessages = nextMessages.map((m) => ({ role: m.role, content: m.content }));
      const replyTextRaw = await aiChat(apiMessages);

      const replyText = (replyTextRaw ?? "").replaceAll("/n", "\n");

      const botMsg: ChatMsg = { id: crypto.randomUUID(), role: "assistant", content: replyText };
      setMessages((prev) => [...prev, botMsg]);
      scrollToBottom();
    } catch (e: any) {
      console.error("AI axios error:", e);

      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "unknown";

      const botMsg: ChatMsg = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: `Error: ${msg}`,
      };

      setMessages((prev) => [...prev, botMsg]);
      scrollToBottom();
    } finally {
      setIsSending(false);
    }
  }

  return (
    <div className="flex h-[520px] flex-col">
      <div className="flex-1 overflow-y-auto rounded-2xl border border-black/10 bg-white p-4">
        <div className="flex flex-col gap-3">
          {messages.map((m) => (
            <div
              key={m.id}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={[
                  "max-w-[85%] rounded-2xl px-4 py-2 text-sm leading-relaxed whitespace-pre-line",
                  m.role === "user"
                    ? "bg-blue-600 text-white rounded-br-md"
                    : "bg-gray-100 text-gray-900 rounded-bl-md",
                ].join(" ")}
              >
                {m.content}
              </div>
            </div>
          ))}

          {isSending && (
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-2xl rounded-bl-md bg-gray-100 px-4 py-2 text-sm text-gray-700">
                Typing…
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <input
          className="flex-1 rounded-xl border border-black/10 px-3 py-2 text-sm outline-none focus:border-blue-400"
          placeholder="Type a message…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void sendMessage();
            }
          }}
          disabled={isSending}
        />

        <Button
          type="primary"
          onClick={() => void sendMessage()}
          disabled={!canSend}
          loading={isSending}
          className="rounded-xl"
        >
          Send
        </Button>
      </div>

    </div>
  );
};

export default AiBot;